import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom/client";
import * as datefns from "date-fns";
import RpaReservationStepActivity, { fetchActivities } from "./RpaReservationStepActivity";
import RpaReservationStepSportType from "./RpaReservationStepSportType";
import RpaReservationStepResource from "./RpaReservationStepResource";
import RpaReservationStepDate from "./RpaReservationStepDate";
import RpaReservationStepScheduleGrid from "./RpaReservationStepScheduleGrid";

/** @typedef {import('../../services/RpaPlanningService.js').RpaResourcesPerDayObj } RpaResourcesPerDayObj */

/**
 * @typedef {object} RpaReservationStepProps
 * @property {?number} [initialActivityId]
 * @property {?number} [initialResourceId]
 * @property {?string} [initialDayDateStr]
 * @property {'activity'|'sporttype'} [stepBy]
 */

/**
 * Composant de réservation rapide RPA pour le frontend
 * @param {RpaReservationStepProps} props
 */
export default function RpaReservationStep({ initialActivityId = null, initialResourceId = null, initialDayDateStr = null, stepBy = "activity" }) {
  const isSportTypeMode = stepBy === "sporttype";
  const [planningDays, setPlanningDays] = useState(/** @type {Array<RpaResourcesPerDayObj>} */ ([]));
  const [resourceIdsOrder, setResourceIdsOrder] = useState(/** @type {Array<number>} */ ([]));
  const [activities, setActivities] = useState(/** @type {Array<import('../../models/RpaActivityModel.js').RpaActivityItem>} */ ([]));
  const [selectedSportType, setSelectedSportType] = useState(/** @type {?string} */ (null));
  const [selectedActivityId, setSelectedActivityId] = useState(initialActivityId);
  const [selectedResourceId, setSelectedResourceId] = useState(initialResourceId);
  const [selectedDateStr, setSelectedDateStr] = useState(initialDayDateStr);
  const [transportAndParkingOption, setTransportAndParkingOption] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const selectedResourceIds = useMemo(() => {
    if (!isSportTypeMode) {
      return selectedResourceId ? [Number(selectedResourceId)] : [];
    }

    if (!selectedSportType) {
      return [];
    }

    const activityIds = activities.filter(rpaActivity => rpaActivity.activity_type === selectedSportType).map(rpaActivity => Number(rpaActivity.id));
    const resourceIds = new Set();

    planningDays.forEach(day => {
      day.rpaResources.forEach(resource => {
        if (activityIds.includes(Number(resource.rpaActivityId))) {
          resourceIds.add(Number(resource.id));
        }
      });
    });

    return [...resourceIds];
  }, [isSportTypeMode, selectedResourceId, selectedSportType, activities, planningDays]);

  useEffect(() => {
    let isMounted = true;

    async function fetchInitialData() {
      setError("");

      try {
        const todayDateStr = datefns.format(new Date(), "dd/MM/yyyy");
        const optionsObj = await fetchOptions();
        const [planningObj, filterObj] = await Promise.all([fetchPlanningObj(todayDateStr, "threeweeks"), fetchFilterObj(true, todayDateStr)]);

        if (!isMounted) {
          return;
        }

        setPlanningDays(planningObj.rpaResourcesPerDays || []);
        setResourceIdsOrder((filterObj.rpaResources || []).map(resource => Number(resource.rpaResourceId)));
        setTransportAndParkingOption(Boolean(optionsObj.transportAndParkingOption));
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError instanceof Error ? fetchError.message : "Impossible de charger les données de réservation.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isSportTypeMode) {
      return undefined;
    }

    let isMounted = true;

    fetchActivities()
      .then(rpaActivities => {
        if (isMounted) {
          setActivities(rpaActivities);
        }
      })
      .catch(fetchError => {
        if (isMounted) {
          setError(fetchError instanceof Error ? fetchError.message : "Impossible de charger les activités.");
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isSportTypeMode]);

  const onActivityChange = useCallback(rpaActivityId => {
    setSelectedActivityId(rpaActivityId);
    setSelectedResourceId(null);
    setSelectedDateStr(null);
  }, []);

  const onSportTypeChange = useCallback(sportType => {
    setSelectedSportType(sportType);
    setSelectedDateStr(null);
  }, []);

  const onResourceChange = useCallback(rpaResourceId => {
    setSelectedResourceId(rpaResourceId);
    setSelectedDateStr(null);
  }, []);

  if (isSportTypeMode) {
    return (
      <div>
        <RpaReservationStepSportType activities={activities} planningDays={planningDays} selectedSportType={selectedSportType} onSportTypeChange={onSportTypeChange} />
        <div className="space--16" />
        {loading ? <p className="paragraph paragraph--no-margin paragraph--grey-900">Chargement du planning...</p> : null}
        {error ? (
          <div className="alert alert--danger">
            <span className="material-symbols-outlined">error</span>
            <div className="alert__content">
              <div className="alert__text">{error}</div>
            </div>
          </div>
        ) : null}

        {!loading && !error ? (
          <>
            <RpaReservationStepDate planningDays={planningDays} selectedResourceIds={selectedResourceIds} selectedDateStr={selectedDateStr} initialDayDateStr={initialDayDateStr} onDateChange={setSelectedDateStr} />
            <RpaReservationStepScheduleGrid planningDays={planningDays} selectedResourceIds={selectedResourceIds} selectedDateStr={selectedDateStr} transportAndParkingOption={transportAndParkingOption} showResourceName />
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <RpaReservationStepActivity planningDays={planningDays} selectedActivityId={selectedActivityId} onActivityChange={onActivityChange} />
      <div className="space--16" />
      {loading ? <p className="paragraph paragraph--no-margin paragraph--grey-900">Chargement du planning...</p> : null}
      {error ? (
        <div className="alert alert--danger">
          <span className="material-symbols-outlined">error</span>
          <div className="alert__content">
            <div className="alert__text">{error}</div>
          </div>
        </div>
      ) : null}

      {!loading && !error ? (
        <>
          <RpaReservationStepResource planningDays={planningDays} resourceIdsOrder={resourceIdsOrder} selectedActivityId={selectedActivityId} selectedResourceId={selectedResourceId} onResourceChange={onResourceChange} />
          <RpaReservationStepDate planningDays={planningDays} selectedResourceIds={selectedResourceIds} selectedDateStr={selectedDateStr} initialDayDateStr={initialDayDateStr} onDateChange={setSelectedDateStr} />
          <RpaReservationStepScheduleGrid planningDays={planningDays} selectedResourceIds={selectedResourceIds} selectedDateStr={selectedDateStr} transportAndParkingOption={transportAndParkingOption} />
        </>
      ) : null}
    </div>
  );
}

/**
 * Récupère les options du planning RPA
 * @returns {Promise<object>}
 */
async function fetchOptions() {
  return fetchRpaData("/api/rpa/search/options");
}

/**
 * Récupère l'objet du planning RPA
 * @param {string} dayDateStr - Date de début du planning
 * @param {'day'|'threedays'|'week'|'threeweeks'|'month'} displayMode - Période à charger
 * @returns {Promise<{rpaResourcesPerDays: Array<RpaResourcesPerDayObj>}>}
 */
async function fetchPlanningObj(dayDateStr, displayMode) {
  return fetchRpaData(`/api/rpa/search/planning?dayDateStr=${encodeURIComponent(dayDateStr)}&view=front&displayMode=${displayMode}`);
}

/**
 * Récupère les filtres et l'ordre des ressources du planning
 * @param {boolean} loadRpaResources - Charger les ressources
 * @param {string} dayDateStr - Date du planning
 * @returns {Promise<object>}
 */
async function fetchFilterObj(loadRpaResources, dayDateStr) {
  return fetchRpaData(`/api/rpa/search/filter?loadRpaResources=${loadRpaResources}&dayDateStr=${encodeURIComponent(dayDateStr)}`);
}

/**
 * Exécute une requête vers l'API RPA
 * @param {string} url - URL de l'API
 * @returns {Promise<any>}
 */
async function fetchRpaData(url) {
  const response = await fetch(url, {
    headers: {
      Authorization: "gestiaweb"
    }
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les données de réservation (${response.status}).`);
  }

  return response.json();
}

Array.from(document.getElementsByClassName("rpa-reservation-step")).forEach(rpaReservationStepNode => {
  const root = ReactDOM.createRoot(rpaReservationStepNode);
  const initialActivityId = parseDataId(rpaReservationStepNode.getAttribute("data-rpaactivityid"));
  const initialResourceId = parseDataId(rpaReservationStepNode.getAttribute("data-rparesourceid"));
  const initialDayDateStr = rpaReservationStepNode.getAttribute("data-daydatestr");

  const stepBy = rpaReservationStepNode.getAttribute("data-stepby") === "sporttype" ? "sporttype" : "activity";

  root.render(<RpaReservationStep initialActivityId={initialActivityId} initialResourceId={initialResourceId} initialDayDateStr={initialDayDateStr} stepBy={stepBy} />);
});

/**
 * Convertit un identifiant HTML optionnel
 * TU NONEED 🧊
 * @param {?string} value - Valeur de l'attribut
 * @returns {?number}
 */
function parseDataId(value) {
  if (!value) {
    return null;
  }

  const id = parseInt(value, 10);

  return Number.isNaN(id) ? null : id;
}
